import axios from 'axios';
import { Browser } from '@capacitor/browser';
import store from '@/store';
import { auth0, getAccessTokenSilentlyWithMonitoring } from '../auth0';
import { createLogoutUri } from '../redirect-uri';
import { isHogcApiUrl } from './hogc-api-origin';
import { openAuthUrl } from '../safe-browser-open';

/**
 * Axios response interceptor for 401 Unauthorized responses.
 *
 * A 401 from a HOGC API is recoverable far more often than it is terminal: the
 * store's token is plain in-memory Vuex, so it is empty for the whole route-guard
 * phase of every page load, and any call made in that window goes out with no
 * usable Bearer. This interceptor used to read all of those as "session over" and
 * log the member out — one feature's mistimed request destroyed the session for
 * the whole app. That is how enabling the `courses` flag in production started
 * logging members out of HOGC One.
 *
 * So: refresh the token once and replay the request. Log out only when the
 * refresh itself fails (the genuine session-ended signal) or when the replay is
 * rejected too. Replaying a 401'd request is safe regardless of method — auth is
 * rejected in a guard, before any handler runs, so nothing was applied.
 *
 * Setup:
 * Call init401ErrorInterceptor() in main.js after auth0 is initialized
 */

const RETRY_FLAG = '__hogcRetriedAfter401';

let responseInterceptorId = null;
let browserFinishedListener = null;

const UNREACHABLE_CODES = ['timeout', 'request_error'];

/**
 * Did the refresh fail because Auth0 could not be REACHED, rather than because it
 * refused? auth0-spa-js reports a refusal as an error carrying an OAuth `error`
 * code (`login_required`, `invalid_grant`, `missing_refresh_token`, …) — that is
 * the session-ended signal. A request that never got an answer surfaces as a bare
 * Error: its own "Timeout when executing 'fetch'" after 10 s x 3 attempts, or the
 * WebView's TypeError ("Load failed" on iOS, "Failed to fetch" on Android).
 *
 * Logging out on those turned a stalled connection to Auth0 into a lost session,
 * and the app now shows its first screen before the launch refresh finishes (see
 * src/utils/launch-auth-budget.js), so a stall reaches this interceptor. An
 * explicit allow-list: anything not recognised as unreachable still logs out, as
 * before.
 *
 * Two Auth0-side failures count as unreachable too, because neither is a refusal:
 * `request_error` is the code auth0-spa-js substitutes when an error response
 * carries no OAuth `error` field (a 5xx from the tenant or edge), and a
 * SyntaxError is a non-JSON body — a proxy or CDN error page. A real refusal
 * always names its OAuth code.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export function isAuth0Unreachable(error) {
  if (!error || typeof error !== 'object') return false;
  if (typeof error.error === 'string') return UNREACHABLE_CODES.includes(error.error);
  if (error instanceof TypeError || error instanceof SyntaxError) return true;
  return /timeout when executing 'fetch'|failed to fetch|load failed|network request failed/i.test(
    error.message || '',
  );
}

/** Force a network refresh of the access token and store it. Throws on failure. */
async function refreshAccessToken() {
  const token = await getAccessTokenSilentlyWithMonitoring({ cacheMode: 'off' }, '401-retry');
  if (!token) throw new Error('Silent refresh returned no token');
  store.commit('user/setToken', token);
  return token;
}

async function logoutAfterUnrecoverable401(source) {
  // Clear analytics sessions before logout
  const { clearAnalyticsSessions } = await import('../clear-analytics-sessions');
  await clearAnalyticsSessions({ preserveAnalyticsIdentity: true });

  // Note: App.vue already has an appUrlOpen listener that handles logout URLs
  // and closes the browser. We only need to listen for browserFinished (iOS)
  // to ensure browser closes even if appUrlOpen doesn't fire for some reason.

  // Clean up any existing listener first to prevent duplicates
  if (browserFinishedListener) {
    browserFinishedListener.remove();
    browserFinishedListener = null;
  }

  // Setup listener for browser finished event (iOS fallback)
  // App.vue's appUrlOpen listener will handle closing the browser for logout URLs,
  // but this ensures it closes on iOS even if appUrlOpen doesn't fire
  // Note: This listener will coexist with login page's browserFinished listener,
  // but they won't conflict since login page listener only updates state
  browserFinishedListener = await Browser.addListener('browserFinished', async () => {
    try {
      // Only close browser if we're not on login page (to avoid conflicts)
      // Login page has its own browserFinished handler
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login')) {
        try {
          await Browser.close();
          console.log('[401 Error] Browser closed via browserFinished event');
        } catch (closeError) {
          // Ignore "No active window" errors - browser may already be closed
          // This can happen when the browser was closed by App.vue's handler or user action
          if (
            closeError?.message?.includes('No active window') ||
            closeError?.message?.includes('No active window to close')
          ) {
            console.log('[401 Error] Browser already closed, ignoring error');
          } else {
            console.error('[401 Error] Error closing browser:', closeError);
          }
        }
      }
    } catch (err) {
      // Browser might already be closed by App.vue's handler - that's okay
      console.log('[401 Error] Browser already closed or error:', err);
    }
    // Clean up listener
    if (browserFinishedListener) {
      browserFinishedListener.remove();
      browserFinishedListener = null;
    }
  });

  await auth0.logout({
    logoutParams: {
      returnTo: createLogoutUri(process.env.VUE_APP_PACKAGE_ID, process.env.VUE_APP_AUTH0_DOMAIN),
    },
    // No hand-off to the OS for a logout. auth0.logout() has already cleared the
    // local session before it calls openUrl, so a logout page that cannot be
    // shown in-app costs nothing — whereas handing it to the OS throws the
    // member out to Safari, and in the #1965 case a login browser is already up.
    openUrl: url => openAuthUrl(url, { source, handOffToOs: false }),
  });
}

/**
 * Log out at most once, and never throw into the request that triggered it.
 *
 * Both matter, and both were missing:
 *
 * - App.vue fires four homepage prefetches in parallel at launch, so an expired
 *   session produced four 401s, four logouts and four concurrent Browser.open
 *   calls. Only one SFSafariViewController can be presented, so the rest
 *   rejected with "Unable to display URL".
 * - That rejection escaped the handler below, so `return Promise.reject(error)`
 *   was never reached and axios settled the member's DATA request with a
 *   URL-opening error instead of the 401 that actually happened. That is why
 *   getFeaturedCards/getMiddleNavIcons/getAllAnimations all reported it
 *   (hogc-one-mobile#1965).
 *
 * The caller always gets the original 401 back.
 *
 * Exported because main.js has three more logout paths (launch, errorHandler,
 * unhandledrejection). They must share this one in-flight promise, or a launch
 * logout and an interceptor logout still present two browsers at once.
 *
 * The promise is cleared once it settles. A successful logout reloads the app
 * via the /logout deep link, but a member who dismisses the logout browser gets
 * no reload — and without the reset, every later unrecoverable 401 in that
 * process would silently skip its logout.
 *
 * @param {string} [source] - names the call site in logs
 * @returns {Promise<void>} never rejects
 */
let logoutPromise = null;
export function safeLogoutAfterUnrecoverable401(source = '401-logout') {
  // Nobody to log out: never signed in, or a logout already completed (auth0-vue
  // clears isAuthenticated when it does). This is what keeps a straggling 401,
  // arriving after the promise below has been cleared, from opening the logout
  // page a second time — and what stops a signed-out device clearing a cache it
  // never had (#1981).
  if (!logoutPromise && auth0?.isAuthenticated?.value === false) return Promise.resolve();
  if (!logoutPromise) {
    logoutPromise = logoutAfterUnrecoverable401(source)
      .catch(err => {
        console.error('[401 Error] Logout redirect failed, continuing:', err?.message);
      })
      .finally(() => {
        logoutPromise = null;
      });
  }
  return logoutPromise;
}

/**
 * Initialize the 401 error interceptor
 */
export function init401ErrorInterceptor() {
  // Remove existing interceptor if any
  if (responseInterceptorId !== null) {
    axios.interceptors.response.eject(responseInterceptorId);
    responseInterceptorId = null;
  }

  responseInterceptorId = axios.interceptors.response.use(
    response => response, // Pass through successful responses
    async error => {
      if (error?.response?.status !== 401) return Promise.reject(error);

      const config = error.config;

      // A third-party 401 (Storyblok, OneSignal, …) says nothing about the
      // member's HOGC session. Never act on it.
      if (!config || !isHogcApiUrl(config.url, config.baseURL)) {
        return Promise.reject(error);
      }

      // Already replayed once — the refreshed token was rejected too, so this is
      // a real session failure.
      if (config[RETRY_FLAG]) {
        console.warn('[Axios] 401 after token refresh — logging out');
        await safeLogoutAfterUnrecoverable401();
        return Promise.reject(error);
      }

      let token;
      try {
        token = await refreshAccessToken();
      } catch (refreshError) {
        if (isAuth0Unreachable(refreshError)) {
          // Auth0 did not answer — the session may be perfectly fine. Fail this
          // request (the screen shows its own error state) and keep the member
          // signed in; the next request, or the next launch, tries again.
          console.warn(
            '[Axios] 401 and Auth0 unreachable for refresh — not logging out:',
            config.url,
          );
          return Promise.reject(error);
        }
        console.warn(
          '[Axios] 401 and silent token refresh failed — logging out:',
          refreshError?.message,
        );
        await safeLogoutAfterUnrecoverable401();
        return Promise.reject(error);
      }

      console.warn('[Axios] 401 recovered by silent token refresh, replaying:', config.url);
      return axios.request({
        ...config,
        [RETRY_FLAG]: true,
        headers: { ...(config.headers || {}), Authorization: `Bearer ${token}` },
      });
    },
  );
}

/**
 * Remove the 401 error interceptor
 */
export function remove401ErrorInterceptor() {
  if (responseInterceptorId !== null) {
    axios.interceptors.response.eject(responseInterceptorId);
    responseInterceptorId = null;
  }

  logoutPromise = null;

  // Clean up listener
  if (browserFinishedListener) {
    browserFinishedListener.remove();
    browserFinishedListener = null;
  }
}
